import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type { PressKitConfig } from '@/types'
import type { PressKitConfigFormValues } from '@/features/press-kit/types'

interface UseAdminPressKitResult {
  config: PressKitConfig | null
  loading: boolean
  saving: boolean
  error: string | null
  saveConfig: (values: PressKitConfigFormValues) => Promise<void>
  refresh: () => void
}

export function useAdminPressKit(): UseAdminPressKitResult {
  const [config, setConfig] = useState<PressKitConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPressKitService
      .getConfig()
      .then(setConfig)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load press kit config'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const saveConfig = useCallback(async (values: PressKitConfigFormValues): Promise<void> => {
    setSaving(true)
    try {
      await adminPressKitService.saveConfig(values)
      refresh()
    } finally {
      setSaving(false)
    }
  }, [refresh])

  return { config, loading, saving, error, saveConfig, refresh }
}
